// src/pages/startups/ApplyPage.tsx
// The startup programme application: four short steps and a review. Step 1
// proves the founder controls the company mailbox with an emailed code before
// anything else is asked. Answers are kept in this browser until submitted.
import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import Button from '@/components/ui/Button';
import HumanCheck, { HumanCheckRef, needsHumanCheck } from '@/components/auth/HumanCheck';
import { getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Notice } from '@/components/ui/Notice';
import { checkbox, fieldControl, fieldHint, fieldLabel, fieldTextarea } from '@/components/ui/fieldStyles';
import { formatDay, startupsApi, type AddressResult, type ApplicationBody, type StartupMeta } from '@/lib/startups';

const DRAFT_KEY = 'latlens.startupDraft';
const STEPS = ['Company', 'Founder', 'Product and customers', 'Pitch and accelerator', 'Review'] as const;
const OUTSIDE_AFRICA = 'XX';
const inputCls = fieldControl;

interface Draft {
  step: number;
  proved: boolean;
  provedFor: string;
  business_name: string;
  reg_country: string;
  reg_country_other: string;
  reg_number: string;
  address_text: string;
  address_pick: AddressResult | null;
  company_domain: string;
  product_domain: string;
  founder_name: string;
  founder_linkedin: string;
  cofounders: string[];
  customer_countries: string[];
  stage: 'live' | 'mvp' | 'building';
  product_link: string;
  mvp_plan: string;
  pitch: string;
  accelerator: string; // an accelerator id, 'other' or 'none'
  accelerator_other: string;
  accelerator_year: string;
}

const EMPTY: Draft = {
  step: 1, proved: false, provedFor: '', business_name: '', reg_country: '', reg_country_other: '', reg_number: '',
  address_text: '', address_pick: null, company_domain: '', product_domain: '', founder_name: '', founder_linkedin: '',
  cofounders: [], customer_countries: [], stage: 'live', product_link: '', mvp_plan: '', pitch: '', accelerator: '',
  accelerator_other: '', accelerator_year: '',
};

function loadDraft(): Draft {
  try {
    const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) ?? 'null');
    return saved ? { ...EMPTY, ...saved } : EMPTY;
  } catch { return EMPTY; }
}
const saveDraft = (d: Draft) => { try { localStorage.setItem(DRAFT_KEY, JSON.stringify(d)); } catch { /* private window */ } };
const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } };

const domainsKey = (d: Draft) => `${d.company_domain.trim().toLowerCase()}|${d.product_domain.trim().toLowerCase()}`;

function stepReady(d: Draft, step: number): boolean {
  switch (step) {
    case 1: return d.proved && d.provedFor === domainsKey(d) && !!d.business_name.trim() && !!d.reg_country
      && (d.reg_country !== OUTSIDE_AFRICA || /^[A-Za-z]{2}$/.test(d.reg_country_other)) && !!d.reg_number.trim() && d.address_text.trim().length >= 5;
    case 2: return d.founder_name.trim().length >= 2 && !!d.founder_linkedin.trim();
    case 3: return d.customer_countries.length > 0 && (d.stage === 'building' ? d.mvp_plan.trim().length >= 20 : !!d.product_link.trim());
    case 4: return d.pitch.trim().length >= 50 && !!d.accelerator && (d.accelerator === 'none' || !!d.accelerator_year)
      && (d.accelerator !== 'other' || d.accelerator_other.trim().length >= 2);
    default: return true;
  }
}

function toBody(d: Draft): ApplicationBody {
  return {
    business_name: d.business_name.trim(),
    reg_country: d.reg_country,
    reg_country_other: d.reg_country === OUTSIDE_AFRICA ? d.reg_country_other.trim().toUpperCase() : undefined,
    reg_number: d.reg_number.trim(),
    address: d.address_pick
      ? { text: d.address_text.trim(), lat: d.address_pick.lat, lon: d.address_pick.lon, label: d.address_pick.label }
      : { text: d.address_text.trim() },
    company_domain: d.company_domain.trim(),
    product_domain: d.product_domain.trim() || undefined,
    founder_name: d.founder_name.trim(),
    founder_linkedin: d.founder_linkedin.trim(),
    cofounder_linkedins: d.cofounders.map(c => c.trim()).filter(Boolean),
    customer_countries: d.customer_countries,
    stage: d.stage,
    product_link: d.stage === 'building' ? undefined : d.product_link.trim(),
    mvp_plan: d.stage === 'building' ? d.mvp_plan.trim() : undefined,
    pitch: d.pitch.trim(),
    accelerator: d.accelerator === 'none' ? { none: true } : d.accelerator === 'other' ? { other: d.accelerator_other.trim() } : { id: d.accelerator },
    accelerator_year: d.accelerator === 'none' ? undefined : Number(d.accelerator_year),
  };
}

const Field: React.FC<{ id: string; label: string; hint?: string; children: React.ReactNode }> = ({ id, label, hint, children }) => (
  <div>
    <label htmlFor={id} className={fieldLabel}>{label}</label>
    {children}
    {hint && <p className={fieldHint}>{hint}</p>}
  </div>
);

const ApplyPage: React.FC = () => {
  const qc = useQueryClient();
  const meta = useQuery<StartupMeta>({ queryKey: ['startup-meta'], queryFn: startupsApi.meta });
  const [d, setD] = useState<Draft>(loadDraft);
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<AddressResult[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const check = useRef<HumanCheckRef>(null);

  const update = (patch: Partial<Draft>) => setD(prev => { const next = { ...prev, ...patch }; saveDraft(next); return next; });
  const go = (step: number) => { setError(null); update({ step }); window.scrollTo?.(0, 0); };

  // Address suggestions from Latlens search, once the founder pauses typing.
  useEffect(() => {
    const q = d.address_text.trim();
    if (q.length < 3 || d.address_pick?.label === q) { setAddresses([]); return; }
    const t = setTimeout(() => { startupsApi.searchAddress(q).then(setAddresses).catch(() => setAddresses([])); }, 400);
    return () => clearTimeout(t);
  }, [d.address_text, d.address_pick]);

  if (meta.isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-6" aria-hidden="true">
        <div><div className="skeleton h-8 w-56" /><div className="skeleton mt-2 h-4 w-96 max-w-full" /></div>
        <div className="skeleton h-1 w-full" />
        <div className="skeleton h-96 rounded-2xl" />
      </div>
    );
  }
  if (meta.isError || !meta.data) return <div className="mx-auto max-w-2xl"><Notice tone="error">{getErrorMessage(meta.error)}</Notice></div>;
  const m = meta.data;

  const shell = (body: React.ReactNode) => (
    <div className="mx-auto max-w-2xl space-y-6">
      <PageHeader title="Startup programme" description="₦200,000 of Latlens credit for African startups building on maps, search and routing." />
      {body}
    </div>
  );

  if (submitted || m.application) {
    return shell(
      <section className="flex items-start gap-4 rounded-2xl border border-border bg-surface p-6">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-success/10 text-success">
          <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-foreground">Application received</h2>
          <p className="mt-1 text-sm text-muted-foreground">A programme officer will read it and we will email you with the decision.</p>
        </div>
      </section>,
    );
  }

  if (!m.cohort) {
    return shell(
      <section className="rounded-2xl border border-border bg-surface p-6">
        <h2 className="text-lg font-semibold tracking-tight text-foreground">Applications are closed</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {m.next ? `Applications open again on ${formatDay(m.next.opens_at)}.` : 'We will announce the next cohort on latlens.com/startups.'}
        </p>
      </section>,
    );
  }

  const registry = m.countries.find(c => c.code === d.reg_country)?.registry_label ?? 'Company registration number';
  const sendCode = async () => {
    setBusy(true); setError(null);
    try {
      await startupsApi.sendCode({ company_domain: d.company_domain.trim(), product_domain: d.product_domain.trim() || undefined });
      setCodeSent(true);
    } catch (e) { setError(getErrorMessage(e)); } finally { setBusy(false); }
  };
  const verifyCode = async () => {
    setBusy(true); setError(null);
    try {
      await startupsApi.verifyCode(code.trim());
      update({ proved: true, provedFor: domainsKey(d) });
    } catch (e) { setError(getErrorMessage(e)); } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await startupsApi.submit(toBody(d), token);
      clearDraft();
      setSubmitted(true);
      qc.invalidateQueries({ queryKey: ['startup-meta'] });
    } catch (e) {
      check.current?.reset();
      // The gateway's proof of the mailbox lasts a day: back to step 1 for a new code.
      if ((e as { response?: { data?: { code?: string } } }).response?.data?.code === 'email_unproved') {
        update({ proved: false, provedFor: '', step: 1 });
        setCodeSent(false);
      }
      setError(getErrorMessage(e));
    } finally { setBusy(false); }
  };

  const proved = d.proved && d.provedFor === domainsKey(d);
  const step = Math.min(Math.max(d.step, 1), 5);
  const nav = (label: string) => (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
      {step > 1 ? <Button variant="outline" onClick={() => go(step - 1)}>Back</Button> : <span />}
      <Button onClick={() => go(step + 1)} disabled={!stepReady(d, step)}>{label}</Button>
    </div>
  );

  return shell(
    <>
      <div className="space-y-3">
      <ol className="flex gap-1.5" aria-label={`Step ${step} of ${STEPS.length}: ${STEPS[step - 1]}`}>
        {STEPS.map((s, i) => (
          <li key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i < step ? 'bg-accent' : 'bg-surface-raised'}`} aria-current={i + 1 === step ? 'step' : undefined}>
            <span className="sr-only">{s}</span>
          </li>
        ))}
      </ol>
      <p className="text-sm text-muted-foreground">Step {step} of {STEPS.length}: {STEPS[step - 1]} · {m.cohort.name} cohort, closes {formatDay(m.cohort.closes_at)}</p>
      </div>

      <Panel title={STEPS[step - 1]} bodyClassName="space-y-5">
        {step === 1 && (
          <>
            <Field id="business_name" label="Registered business name">
              <input id="business_name" className={inputCls} value={d.business_name} onChange={e => update({ business_name: e.target.value })} />
            </Field>
            <Field id="reg_country" label="Country of registration">
              <select id="reg_country" className={inputCls} value={d.reg_country} onChange={e => update({ reg_country: e.target.value })}>
                <option value="">Choose a country</option>
                {m.countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                <option value={OUTSIDE_AFRICA}>Registered outside Africa</option>
              </select>
            </Field>
            {d.reg_country === OUTSIDE_AFRICA && (
              <Field id="reg_country_other" label="Country code" hint="Two letters, like US or GB.">
                <input id="reg_country_other" className={inputCls} maxLength={2} value={d.reg_country_other} onChange={e => update({ reg_country_other: e.target.value })} />
              </Field>
            )}
            <Field id="reg_number" label={registry}>
              <input id="reg_number" className={inputCls} value={d.reg_number} onChange={e => update({ reg_number: e.target.value })} />
            </Field>
            <Field id="address" label="Business address" hint={d.address_pick ? `On the map: ${d.address_pick.label}` : 'Choose your address from the list so we can place it on the map.'}>
              <input id="address" className={inputCls} value={d.address_text} autoComplete="off"
                onChange={e => update({ address_text: e.target.value, address_pick: null })} />
              {addresses.length > 0 && (
                <ul className="mt-1.5 overflow-hidden rounded-xl border border-border bg-surface-raised text-sm shadow-lg shadow-black/10" role="listbox" aria-label="Address suggestions">
                  {addresses.map(a => (
                    <li key={`${a.lat},${a.lon}`}>
                      <button type="button" className="w-full px-3.5 py-2.5 text-left text-foreground hover:bg-surface" role="option" aria-selected={false}
                        onClick={() => { update({ address_text: a.label, address_pick: a }); setAddresses([]); }}>{a.label}</button>
                    </li>
                  ))}
                </ul>
              )}
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field id="company_domain" label="Company domain" hint="Like acme.com">
                <input id="company_domain" className={inputCls} value={d.company_domain} onChange={e => update({ company_domain: e.target.value })} />
              </Field>
              <Field id="product_domain" label="Product domain (if different)" hint="Like getfoo.io">
                <input id="product_domain" className={inputCls} value={d.product_domain} onChange={e => update({ product_domain: e.target.value })} />
              </Field>
            </div>
            <div className="space-y-3 rounded-xl border border-border bg-background/40 p-4">
              <p className="text-sm text-foreground">Your account email must be on one of these domains. We email it a code to confirm.</p>
              {proved ? (
                <p className="flex items-center gap-2 text-sm text-foreground"><CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" /> Email confirmed</p>
              ) : (
                <>
                  <Button variant="outline" onClick={sendCode} disabled={busy || !d.company_domain.trim()}>
                    {codeSent ? 'Send a new code' : 'Email me a code'}
                  </Button>
                  {codeSent && (
                    <div className="flex flex-wrap items-end gap-2">
                      <Field id="code" label="Code from the email">
                        <input id="code" className={`${inputCls} w-36 font-mono tracking-widest`} inputMode="numeric" maxLength={6} value={code} onChange={e => setCode(e.target.value)} />
                      </Field>
                      <Button className="h-11" onClick={verifyCode} disabled={busy || code.trim().length !== 6}>Confirm code</Button>
                    </div>
                  )}
                </>
              )}
            </div>
            {error && <Notice tone="error">{error}</Notice>}
            {nav('Next: Founder')}
          </>
        )}

        {step === 2 && (
          <>
            <Field id="founder_name" label="Founder name">
              <input id="founder_name" className={inputCls} value={d.founder_name} onChange={e => update({ founder_name: e.target.value })} />
            </Field>
            <Field id="founder_linkedin" label="Founder LinkedIn profile" hint="Like https://www.linkedin.com/in/your-name">
              <input id="founder_linkedin" className={inputCls} value={d.founder_linkedin} onChange={e => update({ founder_linkedin: e.target.value })} />
            </Field>
            {d.cofounders.map((c, i) => (
              <Field key={i} id={`cofounder_${i}`} label={`Co-founder ${i + 1} LinkedIn profile`}>
                <input id={`cofounder_${i}`} className={inputCls} value={c}
                  onChange={e => update({ cofounders: d.cofounders.map((x, j) => (j === i ? e.target.value : x)) })} />
              </Field>
            ))}
            {d.cofounders.length < 3 && (
              <button type="button" className="text-sm font-medium text-accent-text hover:underline" onClick={() => update({ cofounders: [...d.cofounders, ''] })}>Add a co-founder</button>
            )}
            {nav('Next: Product and customers')}
          </>
        )}

        {step === 3 && (
          <>
            <fieldset className="space-y-2">
              <legend className={fieldLabel}>Countries your customers are in</legend>
              <div className="grid max-h-56 grid-cols-2 gap-x-3 gap-y-2 overflow-y-auto rounded-xl border border-border p-3 sm:grid-cols-3">
                {m.countries.map(c => (
                  <label key={c.code} className="flex items-center gap-2 text-sm text-foreground">
                    <input type="checkbox" className={checkbox} checked={d.customer_countries.includes(c.code)}
                      onChange={e => update({ customer_countries: e.target.checked ? [...d.customer_countries, c.code] : d.customer_countries.filter(x => x !== c.code) })} />
                    {c.name}
                  </label>
                ))}
              </div>
            </fieldset>
            <Field id="stage" label="Stage">
              <select id="stage" className={inputCls} value={d.stage} onChange={e => update({ stage: e.target.value as Draft['stage'] })}>
                <option value="live">Live product</option>
                <option value="mvp">MVP</option>
                <option value="building">Building one</option>
              </select>
            </Field>
            {d.stage === 'building' ? (
              <Field id="mvp_plan" label="How you will reach an MVP" hint={`${d.mvp_plan.length}/600`}>
                <textarea id="mvp_plan" className={fieldTextarea} rows={4} maxLength={600} value={d.mvp_plan} onChange={e => update({ mvp_plan: e.target.value })} />
              </Field>
            ) : (
              <Field id="product_link" label="Product link">
                <input id="product_link" className={inputCls} value={d.product_link} onChange={e => update({ product_link: e.target.value })} />
              </Field>
            )}
            {nav('Next: Pitch and accelerator')}
          </>
        )}

        {step === 4 && (
          <>
            <Field id="pitch" label="Pitch" hint={`What you build, for whom, and how Latlens fits. ${d.pitch.length}/1,500`}>
              <textarea id="pitch" className={fieldTextarea} rows={6} maxLength={1500} value={d.pitch} onChange={e => update({ pitch: e.target.value })} />
            </Field>
            <Field id="accelerator" label="Accelerator">
              <select id="accelerator" className={inputCls} value={d.accelerator} onChange={e => update({ accelerator: e.target.value })}>
                <option value="">Choose one</option>
                {m.accelerators.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                <option value="other">Other</option>
                <option value="none">None</option>
              </select>
            </Field>
            {d.accelerator === 'other' && (
              <Field id="accelerator_other" label="Accelerator name">
                <input id="accelerator_other" className={inputCls} value={d.accelerator_other} onChange={e => update({ accelerator_other: e.target.value })} />
              </Field>
            )}
            {d.accelerator && d.accelerator !== 'none' && (
              <Field id="accelerator_year" label="Year attended">
                <input id="accelerator_year" className={`${inputCls} w-32`} inputMode="numeric" maxLength={4} value={d.accelerator_year} onChange={e => update({ accelerator_year: e.target.value })} />
              </Field>
            )}
            {nav('Review')}
          </>
        )}

        {step === 5 && (
          <>
            <p className="text-sm text-muted-foreground">Check your application before you send it.</p>
            <dl className="divide-y divide-border rounded-xl border border-border text-sm">
              {([
                ['Business', `${d.business_name} · ${registry} ${d.reg_number}`, 1],
                ['Address', d.address_pick?.label ?? d.address_text, 1],
                ['Domains', [d.company_domain, d.product_domain].filter(Boolean).join(', '), 1],
                ['Founder', `${d.founder_name} · ${d.founder_linkedin}`, 2],
                ['Customers in', d.customer_countries.map(c => m.countries.find(x => x.code === c)?.name ?? c).join(', '), 3],
                ['Stage', d.stage === 'building' ? `Building one: ${d.mvp_plan}` : `${d.stage === 'live' ? 'Live product' : 'MVP'}: ${d.product_link}`, 3],
                ['Accelerator', d.accelerator === 'none' ? 'None' : `${d.accelerator === 'other' ? d.accelerator_other : m.accelerators.find(a => a.id === d.accelerator)?.name ?? ''} (${d.accelerator_year})`, 4],
                ['Pitch', d.pitch, 4],
              ] as [string, string, number][]).map(([k, v, s]) => (
                <div key={k} className="grid gap-1 px-4 py-3 sm:grid-cols-[10rem_minmax(0,1fr)_auto] sm:gap-4">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="whitespace-pre-line break-words text-foreground">{v}</dd>
                  <dd><button type="button" className="text-xs font-medium text-accent-text hover:underline" onClick={() => go(s)}>Edit</button></dd>
                </div>
              ))}
            </dl>
            <p className="text-xs text-muted-foreground">We keep your application to run the programme. Only the programme team reads it.</p>
            <HumanCheck ref={check} onToken={setToken} />
            {error && <Notice tone="error">{error}</Notice>}
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-5">
              <Button variant="outline" onClick={() => go(4)}>Back</Button>
              {/* Off until the invisible browser check hands back a token. */}
              <Button onClick={submit} disabled={busy || (needsHumanCheck && !token)}>Submit application</Button>
            </div>
          </>
        )}
      </Panel>
    </>,
  );
};

export default ApplyPage;
